import React, { useCallback } from 'react';
import { Text, Modal, ModalCloseButton, ModalBody, ModalHeader, ModalFooterButtons } from '@televet/kibble-ui';
import { useServiceReminders } from './useServiceReminders';
import useClinicUser from 'shared/hooks/useClinicUser';
import { useTracking } from 'shared/providers/TrackingProvider';
import { ServiceReminderEvents } from 'shared/providers/TrackingProvider/EventNames';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';

interface IEnableRemindersModal {
  isOpen: boolean;
  onClose: () => void;
}

const EnableRemindersModal = ({ isOpen, onClose }: IEnableRemindersModal): JSX.Element | null => {
  const { currentClinicId, currentClinic } = useClinicUser();
  const { serviceReminderDataLoading, clinicHasServiceReminders, toggleClinicHasServiceReminders } =
    useServiceReminders(currentClinicId);
  const { primaryIntegration, primaryIntegrationName } = useIntegrationsProvider();
  const { trackEvent } = useTracking();

  const handleConfirm = useCallback((): void => {
    toggleClinicHasServiceReminders();
    trackEvent(
      clinicHasServiceReminders
        ? ServiceReminderEvents.DisableServiceReminders
        : ServiceReminderEvents.EnableServiceReminders,
      {
        clinicId: currentClinicId,
        integrationType: primaryIntegration?.type,
        clinicName: currentClinic?.name,
        pims: primaryIntegrationName || '',
      },
    );
    onClose();
  }, [
    clinicHasServiceReminders,
    currentClinic?.name,
    currentClinicId,
    onClose,
    primaryIntegration?.type,
    primaryIntegrationName,
    toggleClinicHasServiceReminders,
    trackEvent,
  ]);

  return (
    <Modal size="md" isOpen={isOpen} onClose={onClose}>
      <ModalHeader size="lg">{clinicHasServiceReminders ? 'Disable' : 'Enable'} Reminders</ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        <Text as="p" mb={5}>
          {clinicHasServiceReminders
            ? 'Disabling service reminders will stop sending all service reminders regardless of status.'
            : 'Enabling service reminders will allow active and scheduled service reminders to send normally.'}
        </Text>
        <Text fontWeight="bold">
          Are you sure you want to {clinicHasServiceReminders ? 'disable' : 'enable'} reminders?
        </Text>
      </ModalBody>
      <ModalFooterButtons
        isDestructive={clinicHasServiceReminders}
        submitText={clinicHasServiceReminders ? 'Disable Reminders' : 'Enable Reminders'}
        onSubmit={handleConfirm}
        isLoading={serviceReminderDataLoading}
      />
    </Modal>
  );
};

export default EnableRemindersModal;
